/**
 * Sampling Management ▸ Route Configuration — pure helpers for the page
 * (row mapping, Copy To All Cell, change detection, save payload).
 */

export const RC_PAGE_ID = 'routeconfig';
export const RC_FORM_NAME = 'Route Configuration';
export const RC_BASE = '/api/v1/route-configuration';

/** Sampling Type labels, by the stored value (CocSamplingType on the server). */
export const SAMPLING_TYPES = ['Sampling', 'Pick-up'] as const;

export interface RcRoute { oid: string; routeCode: string | null; routeName: string | null }

export interface RcRow {
  key: string;
  cocId: string;
  projectRef: string;
  projectName: string;
  projectCode: string;
  client: string;
  /** Normalised COCSettings.RouteID ('' = none; legacy may hold a ';' list). */
  routeIds: string;
  routeCode: string;
  routeName: string;
  /** '' = not chosen yet, else a SAMPLING_TYPES label. */
  samplingType: string;
}

export interface RcSaveRow {
  oid: string;
  setRoute: boolean;
  routeOid: string | null;
  setType: boolean;
  collectionType: number | null;
}

const s = (v: unknown) => (v == null ? '' : String(v));

/** Lower-cased, ';'-split, trimmed — so a GUID column and a text list compare alike. */
export const normRouteIds = (v: unknown) =>
  s(v).split(';').map((x) => x.trim().toLowerCase()).filter(Boolean).join(';');

export const typeLabel = (v: unknown) =>
  v === 0 || v === '0' ? SAMPLING_TYPES[0] : v === 1 || v === '1' ? SAMPLING_TYPES[1] : '';

export const typeValue = (label: string): number | null => {
  const i = SAMPLING_TYPES.indexOf(label as (typeof SAMPLING_TYPES)[number]);
  return i < 0 ? null : i;
};

export function toRcRow(d: Record<string, unknown>): RcRow {
  return {
    key: s(d.oid).toLowerCase(),
    cocId: s(d.cocId),
    projectRef: s(d.projectRef),
    projectName: s(d.projectName),
    projectCode: s(d.projectCode),
    client: s(d.client),
    routeIds: normRouteIds(d.routeIds),
    routeCode: s(d.routeCodes),
    routeName: s(d.routeNames),
    samplingType: typeLabel(d.collectionType),
  };
}

/** Sets a row's route from a Route lookup entry (null clears it). */
export function withRoute(r: RcRow, route: RcRoute | null): RcRow {
  return route
    ? { ...r, routeIds: route.oid.toLowerCase(), routeCode: s(route.routeCode), routeName: s(route.routeName) }
    : { ...r, routeIds: '', routeCode: '', routeName: '' };
}

/** Right-click "Copy To All Cell": the source row's route or sampling type onto
 *  every row in `keys` (the rows the grid currently shows). */
export function copyToRows(rows: RcRow[], keys: Set<string>, source: RcRow, field: 'route' | 'samplingType'): RcRow[] {
  return rows.map((r) => {
    if (!keys.has(r.key) || r.key === source.key) return r;
    return field === 'route'
      ? { ...r, routeIds: source.routeIds, routeCode: source.routeCode, routeName: source.routeName }
      : { ...r, samplingType: source.samplingType };
  });
}

/** The save payload: one entry per row whose route or sampling type differs from the load. */
export function changedRows(rows: RcRow[], loaded: Map<string, RcRow>): RcSaveRow[] {
  const out: RcSaveRow[] = [];
  for (const r of rows) {
    const o = loaded.get(r.key);
    if (!o) continue;
    const setRoute = r.routeIds !== o.routeIds;
    const setType = r.samplingType !== o.samplingType;
    if (!setRoute && !setType) continue;
    out.push({
      oid: r.key,
      setRoute,
      routeOid: setRoute && r.routeIds ? r.routeIds : null,
      setType,
      collectionType: setType ? typeValue(r.samplingType) : null,
    });
  }
  return out;
}

export function filterRcRows(rows: RcRow[], search: string): RcRow[] {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    [r.cocId, r.projectRef, r.projectName, r.projectCode, r.client, r.routeCode, r.routeName, r.samplingType]
      .some((v) => v.toLowerCase().includes(q)));
}
